import { FormHelperText, Skeleton } from '@mui/material'
import { useEffect, useMemo } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { AsyncSelect } from 'src/components/forms'
import { ITableColumn, TableList } from 'src/components/table'
import { useLoadOptions } from 'src/hooks/useLoadOptions'
import { EntityWithIdAndDescriptionFields } from 'src/types'
import CardActions from 'src/pages/sistema/components/CardActions'
import { useAFOPUAforoTipos } from '../hooks/useAFOPUCache'
import { AFOPURow, useAFOPUTablaValorInicial } from '../hooks/useTablaValorAFOPU'
import { OperacionAFOPUFields, OperacionAFOPUTablaValorFields } from '../types/fields'
import { FormOperacionAFOPU } from '../types/types'
import { OperacionAFOPUTablaValorVistaInicial } from '../types/dto'

interface SeccionNuevoAFOPUTablaValoresProps {
  formMethods: UseFormReturn<FormOperacionAFOPU>
  codSistemaPrincipal: number
}

const SeccionNuevoAFOPUTablaValores = ({ formMethods, codSistemaPrincipal }: SeccionNuevoAFOPUTablaValoresProps) => {
  const { headers, rows, aforos, isLoading } = useAFOPUTablaValorInicial(codSistemaPrincipal);

  const aforoTipos = useAFOPUAforoTipos(codSistemaPrincipal);
  const loadAforoTipos = useLoadOptions({
    data: aforoTipos.aforoTipos,
    loading: aforoTipos.isLoading,
    isError: aforoTipos.isError,
    error: aforoTipos.error
  });

  useEffect(() => {
    if (aforos && aforos.length > 0) {

      // valores de prueba
      const valoresPrueba = ["-5%", "-10%", "-25%", "-50%", "-75%", "-100%"];

      const dataParaFormulario = aforos.map((item: OperacionAFOPUTablaValorVistaInicial) => {

        // para llenar de valores al azar, para pruebas
        const valorOriginal = item[OperacionAFOPUTablaValorFields.Valor];
        const valorFinal = valorOriginal;

        // if (valorFinal === '' || valorFinal === null) {
        //   valorFinal = valoresPrueba[Math.floor(Math.random() * valoresPrueba.length)];
        // }

        return {
          [OperacionAFOPUTablaValorFields.CodInstrumento]: item[OperacionAFOPUTablaValorFields.CodInstrumento],
          [OperacionAFOPUTablaValorFields.DescInstrumento]: item[OperacionAFOPUTablaValorFields.DescInstrumento],
          [OperacionAFOPUTablaValorFields.CodOperacionTipo]: item[OperacionAFOPUTablaValorFields.CodOperacionTipo],
          [OperacionAFOPUTablaValorFields.DescOperacionTipo]: item[OperacionAFOPUTablaValorFields.DescOperacionTipo],
          [OperacionAFOPUTablaValorFields.Valor]: valorFinal as any,
          [OperacionAFOPUTablaValorFields.OrdenInstrumento]: item.ordenInstrumento ?? 0,
        };
      });

      formMethods.setValue(OperacionAFOPUFields.TablaValores, dataParaFormulario);
    }
  }, [aforos, formMethods]);

  // generamos columnas de forma dinamica segun el pivot de los aforos
  const columns: ITableColumn[] = useMemo(() => {
    const cols: ITableColumn[] = [
      {
        label: 'Instrumento',
        onRenderCell: (row: AFOPURow) => <b>{row.descInstrumento}</b>
      }
    ];

    headers.forEach((header) => {
      cols.push({
        label: header.descOperacionTipo,
        onRenderCell: (row: AFOPURow) => {

          const celda = row.celdas[header.codOperacionTipo];
          if (!celda) return <span>-</span>;;

          // nombre del campo interno del formulario
          const fieldName = `${OperacionAFOPUFields.TablaValores}` as const;
          const currentValues = formMethods.watch(fieldName);
          const index = currentValues.findIndex(
            (v: any) =>
              v.codOperacionAFOPUInstrumento === celda.codOperacionAFOPUInstrumento &&
              v.codOperacionAFOPUOperacionTipo === celda.codOperacionAFOPUOperacionTipo
          );

          if (index === -1) return <Skeleton variant='rectangular' />;

          return (
            <AsyncSelect
              loadOptions={loadAforoTipos}
              label=''
              control={formMethods.control}
              name={`${fieldName}.${index}.${OperacionAFOPUTablaValorFields.Valor}`}
              disabled={!celda.permiteEditar}
              valueField={EntityWithIdAndDescriptionFields.Description}
              selectNotNulleable

            />

          );
        }
      });
    });

    return cols;
  }, [headers, formMethods]);

  return (
    <CardActions title='Tabla de Valores' icon='mdi:table-edit' colapsable tableContent>
      <TableList
        entityList={rows}
        columns={columns}
        isLoading={isLoading}
        error={false}
      />
      {formMethods.formState.errors.tablaValores?.message && (
        <FormHelperText error sx={{ px: 2, pb: 1 }}>
          {formMethods.formState.errors.tablaValores.message}
        </FormHelperText>
      )}
    </CardActions>
  );
}

export default SeccionNuevoAFOPUTablaValores